import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import * as Accordion from "@radix-ui/react-accordion";
import { memoize } from "lodash";

import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import { useManifests } from "src/api/connectApi/queries";
import {
  Providers,
  Provider as ProviderType,
  isDatabaseProvider,
  isIntegrationProvider,
  Resource,
  LegacyIntegrationProvider,
} from "src/api/connectApi/types";
import { Icon } from "src/base-components/Icon";
import { CreateConnectionCard } from "src/connections/CreateConnectionCard";
import {
  getProviderName,
  getProviderTags,
} from "src/connections/ProviderResourceProperties";
import { ProviderIcon } from "src/connections/config/Icon";
import {
  ProviderTag,
  GROUPING_CATEGORIES,
  GroupingCategory,
  CATEGORY_DISPLAY_NAME,
} from "src/connections/connectionConfigs";
import {
  FEATURE_FLAGS,
  BETA_CONNECTIONS,
  hasConnectionCreationEnabled,
  isFeatureFlagEnabled,
} from "src/router/featureFlags";
import { normalizeToSortedString } from "src/utils/stringUtils";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type AWSRegionPrefixes = "eu" | "us";

type ProviderRegionMapping = {
  [key in ProviderType]?: AWSRegionPrefixes[];
};

type ResourceRegionMapping = {
  [key in ProviderType]?: {
    [resource in Resource]?: AWSRegionPrefixes[];
  };
};

/**
 * Some providers are only available in certain geographical regions.
 * This mapping defines which regions are available for each provider
 * so that we can disable the provider card if it's not available.
 */
const ProviderRegionAvailability: ProviderRegionMapping = {
  boniversum: ["eu"],
  creditreform: ["eu"],
  crif_b2c: ["eu"],
  crif_b2b: ["eu"],
  crs: ["us"],
  equifax_us_commercial_risk: ["us"],
  equifax_us: ["us"],
  experian_us_b2b: ["us"],
  experian_us: ["us"],
  experian_us_clarity_services: ["us"],
  data_x: ["us"],
  schufa: ["eu"],
  thomson_reuters: ["us"],
  trans_union: ["us"],
};

const migratedLegacyProviderToManifestProviderMapping: Partial<{
  [key in LegacyIntegrationProvider]: string;
}> = {
  creditsafe: "creditsafe_b2b",
  dnb: "dnb_v2",
  experian_us: "experian_us_b2c",
  socure: "socure_v2",
  crs: "crs_v2",
};

export const DeprecatedProviders: ProviderType[] = ["experian_us_b2b"];

const ResourceRegionAvailability: ResourceRegionMapping = {
  plaid: {
    financial_insights_report: ["eu"],
  },
};

const Divider: React.FC = () => (
  <div className="mt-3 h-px w-full rounded-full bg-gray-200"></div>
);

/**
 * Type for the props of the CreateConnectionGrid component which renders
 * the grid of connection cards in the create connection modal.
 */
export type CreateConnectionGridProps = {
  onProviderSelect: (
    provider: ProviderType | string,
    isManifestProvider: boolean,
  ) => void;
};

/**
 * Returns true if the provider is available in the current AWS region.
 * Returns true if the feature flag for powertools is enabled.
 */
export const isProviderAvailableInAWSRegion = (
  provider: ProviderType,
  AWSRegion: string | undefined,
): boolean => {
  if (isFeatureFlagEnabled(FEATURE_FLAGS.powertools)) {
    return true;
  }
  if (!(provider in ProviderRegionAvailability)) {
    return true;
  }
  const isAvailable = ProviderRegionAvailability[provider]?.some(
    (regionPrefix) => AWSRegion?.startsWith(regionPrefix),
  );
  return Boolean(isAvailable);
};

export const isProviderDeprecated = (provider: ProviderType): boolean => {
  return DeprecatedProviders.includes(provider);
};

export const isProviderDeprecatedWithPowertools = (
  provider: ProviderType,
): boolean => {
  if (isFeatureFlagEnabled(FEATURE_FLAGS.powertools)) {
    return false;
  }
  return isProviderDeprecated(provider);
};

export const isResourceAvailableInAwsRegion = (
  provider: ProviderType,
  resource: Resource,
  AWSRegion: string | undefined,
): boolean => {
  if (isFeatureFlagEnabled(FEATURE_FLAGS.powertools)) {
    return true;
  }
  if (!(provider in ResourceRegionAvailability)) {
    return true;
  }
  if (!(resource in ResourceRegionAvailability[provider]!)) {
    return true;
  }
  const isAvailable = ResourceRegionAvailability[provider]![resource]?.some(
    (regionPrefix) => AWSRegion?.startsWith(regionPrefix),
  );
  return Boolean(isAvailable);
};

export const isBetaConnection = (provider: ProviderType | string) => {
  return BETA_CONNECTIONS.includes(provider);
};

const isManifestProviderAvailableInAwsRegion = (
  manifest: ManifestIntegrationProvider,
  AWSRegion: string | undefined,
): boolean => {
  if (
    isFeatureFlagEnabled(FEATURE_FLAGS.powertools) ||
    isFeatureFlagEnabled(FEATURE_FLAGS.integrationVelocity)
  ) {
    return true;
  }
  if (manifest.geographic_restrictions) {
    return manifest.geographic_restrictions.some((regionPrefix) =>
      AWSRegion?.startsWith(regionPrefix),
    );
  }
  return true;
};

/**
 * Returns a list of providers that are available in the current AWS region
 * and have connection creation enabled. For example, CRS is only available
 * in US regions.
 */
const availableProviders = (AWSRegion: string | undefined) => {
  return Providers.filter((provider) => {
    const isEnabled = hasConnectionCreationEnabled(provider);
    const isAvailable = isProviderAvailableInAWSRegion(provider, AWSRegion);
    if (isFeatureFlagEnabled(FEATURE_FLAGS.powertools)) {
      return isEnabled && isAvailable;
    }

    const isDeprecated = isProviderDeprecated(provider);
    return isEnabled && isAvailable && !isDeprecated;
  }).sort((a, b) => a.localeCompare(b));
};

type IntegrationProvider = {
  key: LegacyIntegrationProvider;
  value: string;
  isBeta: boolean;
  isDeprecated: boolean;
  grouping_categories: ProviderTag[];
};

/**
 * Integration providers exclude databases and custom connections.
 * For example, Schufa, Boniversum, etc. are integration providers.
 */
export const integrationProviders = (
  AWSRegion: string | undefined,
): IntegrationProvider[] =>
  availableProviders(AWSRegion)
    .filter((provider) => isIntegrationProvider(provider))
    .map((provider) => ({
      key: provider,
      value: getProviderName(provider),
      isBeta: isBetaConnection(provider),
      isDeprecated: isProviderDeprecated(provider),
      grouping_categories: getProviderTags(provider),
    }));

const isManifestProvider = (
  provider: ManifestIntegrationProvider | IntegrationProvider,
): provider is ManifestIntegrationProvider => {
  return (provider as ManifestIntegrationProvider).version !== undefined;
};

/**
 * Return a list of database providers that are available in the current AWS region.
 */
export const databaseProviders = (AWSRegion: string | undefined) =>
  availableProviders(AWSRegion)
    .filter((provider) => isDatabaseProvider(provider))
    .map((provider) => ({
      key: provider,
      value: getProviderName(provider),
    }));

/**
 * This function is used to generate a unique key for each provider card.
 * It's used to a) identify the card in the VDOM and b) as test data-loc attribute.
 */

export const providerKey = memoize(
  (provider: ProviderType) => `create-${provider}-connection`,
);

interface Provider {
  provider: string;
  display_name: string;
}

export interface ManifestIntegrationProviders {
  [key: string]: Provider;
}

type NestedMap<T> = {
  [key: string]: NestedMap<T> | T[];
};

type AllIntegrationProvider = ManifestIntegrationProvider | IntegrationProvider;

const CreateConnectionGridGrouped: React.FC<CreateConnectionGridProps> = ({
  onProviderSelect,
}) => {
  const workspaceData = useWorkspaceAndOrg();
  const AWSRegion = workspaceData.workspace?.aws_region;
  let manifestIntegrationProviders =
    useManifests(workspaceData.workspace?.base_url).data || [];

  // If integrationVelocity FF is disabled, display only non-`internal` manifest
  // providers available in the region
  manifestIntegrationProviders = manifestIntegrationProviders.filter(
    (provider) => isManifestProviderAvailableInAwsRegion(provider, AWSRegion),
  );
  if (!isFeatureFlagEnabled(FEATURE_FLAGS.integrationVelocity)) {
    manifestIntegrationProviders = manifestIntegrationProviders.filter(
      (provider) =>
        (provider.audience && provider.audience !== "internal") ||
        isFeatureFlagEnabled(provider.name),
    );
  }
  const filteredProviders: AllIntegrationProvider[] = [
    ...integrationProviders(AWSRegion).filter((provider) => {
      if (migratedLegacyProviderToManifestProviderMapping[provider.key]) {
        return !manifestIntegrationProviders.some(
          (manifestProvider) =>
            manifestProvider.name ===
            migratedLegacyProviderToManifestProviderMapping[provider.key],
        );
      }
      return true;
    }),
    ...manifestIntegrationProviders,
  ].sort((a, b) => {
    const aValue = isManifestProvider(a) ? a.display_name : a.value;
    const bValue = isManifestProvider(b) ? b.display_name : b.value;
    return aValue.localeCompare(bValue);
  });

  const DISPLAY_REGIONS_THRESHOLD = 3;

  const getTerritoryDisplayName = (value: string | string[]): string | null => {
    if (Array.isArray(value)) {
      if (value.length > DISPLAY_REGIONS_THRESHOLD) {
        return "Multiple Regions";
      }
      return value.join(", ");
    }
    return value;
  };

  const getTagDisplayName = (tag: ProviderTag): string => {
    if (tag.category === "territory") {
      return normalizeToSortedString(tag.value);
    }
    if (Array.isArray(tag.value)) {
      return tag.value.join(", ");
    }
    return tag.value;
  };

  const getGroupingTags = (provider: AllIntegrationProvider) => {
    return (provider.grouping_categories || []).filter(
      (tag): tag is ProviderTag & { category: GroupingCategory } =>
        GROUPING_CATEGORIES.includes(tag.category as GroupingCategory),
    );
  };

  const groupProvidersByCategories = (
    providers: AllIntegrationProvider[],
  ): NestedMap<AllIntegrationProvider> => {
    const groupedProviders: NestedMap<AllIntegrationProvider> = {};

    providers.forEach((provider) => {
      const groupingTags = getGroupingTags(provider);

      // Group providers without tags directly under "Other"
      if (groupingTags.length === 0) {
        if (!groupedProviders["Other"]) {
          groupedProviders["Other"] = [];
        }
        (groupedProviders["Other"] as AllIntegrationProvider[]).push(provider);
        return;
      }

      let currentLevel = groupedProviders;

      // navigate/create nested structure for all tags except last
      groupingTags.slice(0, -1).forEach((tag) => {
        const tagName = getTagDisplayName(tag);
        if (!currentLevel[tagName]) {
          currentLevel[tagName] = {};
        }
        currentLevel = currentLevel[
          tagName
        ] as NestedMap<AllIntegrationProvider>;
      });

      // add provider to last level
      const lastTagName = getTagDisplayName(
        groupingTags[groupingTags.length - 1],
      );
      if (!currentLevel[lastTagName]) {
        currentLevel[lastTagName] = [];
      }
      (currentLevel[lastTagName] as AllIntegrationProvider[]).push(provider);
    });

    return groupedProviders;
  };

  const groupedProviders = groupProvidersByCategories(filteredProviders);
  const countProviders = (
    val: NestedMap<AllIntegrationProvider> | AllIntegrationProvider[],
  ): number => {
    if (Array.isArray(val)) return val.length;
    return Object.values(val).reduce((sum, v) => sum + countProviders(v), 0);
  };

  // get first provider for icon
  const getFirstProvider = (
    val: NestedMap<AllIntegrationProvider> | AllIntegrationProvider[],
  ): AllIntegrationProvider => {
    if (Array.isArray(val)) return val[0];
    return getFirstProvider(Object.values(val)[0]);
  };

  // to avoid redundancy, get displayname by usecase/product/... tag, else by provider name
  const getProviderDisplayName = (provider: AllIntegrationProvider): string => {
    const tags = provider.grouping_categories || [];

    const productOrUsecaseTag = tags.find(
      (tag) => tag.category === "product" || tag.category === "usecase",
    );

    const segmentTag = tags.find((tag) => tag.category === "segment");

    if (productOrUsecaseTag && segmentTag) {
      const base = Array.isArray(productOrUsecaseTag.value)
        ? productOrUsecaseTag.value.join(", ")
        : productOrUsecaseTag.value;

      const seg = Array.isArray(segmentTag.value)
        ? segmentTag.value.join(", ")
        : segmentTag.value;

      return `${base} - ${seg}`;
    }

    if (productOrUsecaseTag) {
      return Array.isArray(productOrUsecaseTag.value)
        ? productOrUsecaseTag.value.join(", ")
        : productOrUsecaseTag.value;
    }

    if (segmentTag) {
      return Array.isArray(segmentTag.value)
        ? segmentTag.value.join(", ")
        : segmentTag.value;
    }

    const displayTag = tags.find((tag) =>
      CATEGORY_DISPLAY_NAME.includes(
        tag.category as (typeof CATEGORY_DISPLAY_NAME)[number],
      ),
    );

    if (displayTag) {
      return Array.isArray(displayTag.value)
        ? displayTag.value.join(", ")
        : displayTag.value;
    }

    const providerTag = tags.find((tag) => tag.category === "provider");
    if (providerTag) {
      return Array.isArray(providerTag.value)
        ? providerTag.value.join(", ")
        : providerTag.value;
    }

    return isManifestProvider(provider)
      ? provider.display_name
      : provider.value;
  };

  const renderProviderCard = (
    provider: AllIntegrationProvider,
    title: string,
    index: number,
    height: number,
    isLast: boolean,
    showRegionToolTip?: boolean,
    toolTipRegions?: string[],
  ) => {
    if (isManifestProvider(provider)) {
      return (
        <CreateConnectionCard
          key={`${provider.name}-${index}`}
          dataLoc={`manifest-${provider.name}-${provider.audience}`}
          height={height}
          isBeta={provider.audience === "beta"}
          isLast={isLast}
          manifestVersion={provider.version}
          provider={provider.name}
          showRegionToolTip={showRegionToolTip}
          title={title}
          toolTipRegions={toolTipRegions}
          onClick={() => onProviderSelect(provider.name, true)}
        />
      );
    }

    return (
      <CreateConnectionCard
        key={`${providerKey(provider.key)}-${index}`}
        dataLoc={providerKey(provider.key)}
        height={height}
        isBeta={provider.isBeta}
        isDeprecated={provider.isDeprecated}
        isLast={isLast}
        provider={provider.key}
        showRegionToolTip={showRegionToolTip}
        title={title}
        toolTipRegions={toolTipRegions}
        onClick={() => onProviderSelect(provider.key, false)}
      />
    );
  };

  const getTerritoryTag = (provider: AllIntegrationProvider) => {
    const tags = provider?.grouping_categories || [];
    return tags.find((tag) => tag.category === "territory");
  };

  return (
    <>
      <Accordion.Root className="m-0" defaultValue={[]} type="multiple">
        {Object.entries(groupedProviders)
          .sort(([a], [b]) => {
            // "Other" should always be last
            if (a === "Other") return 1;
            if (b === "Other") return -1;
            return a.localeCompare(b);
          })
          .map(([providerName, territories], providerIndex) => {
            const totalCount = countProviders(territories);
            const firstProvider = getFirstProvider(territories);

            return (
              <Accordion.Item
                key={providerName}
                className={providerIndex > 0 ? "mt-4" : ""}
                value={providerName}
              >
                <div className="flex w-full flex-col">
                  {/* level 1: provider accordion (e.g. "Equifax") */}
                  <Accordion.Header className="h-[58px]">
                    <Accordion.Trigger className="group w-full">
                      <div className="box-border h-full w-full rounded-lg border border-gray-200 transition-all hover:cursor-pointer hover:border-indigo-400">
                        <div className="flex items-center pl-2">
                          <div
                            className="mr-2 w-10 pt-2"
                            style={{ height: `56px` }}
                          >
                            <ProviderIcon
                              manifestVersion={
                                isManifestProvider(firstProvider)
                                  ? firstProvider.version
                                  : undefined
                              }
                              provider={
                                isManifestProvider(firstProvider)
                                  ? firstProvider.name
                                  : firstProvider.key
                              }
                              size="xl"
                            />
                          </div>
                          <div className="flex flex-col items-start">
                            <div className="text-gray-800 font-inter-semibold-12px">
                              {providerName}
                            </div>
                            <div className="-mt-0.5 text-gray-500 font-inter-normal-12px">
                              {totalCount}{" "}
                              {totalCount === 1 ? "service" : "services"}
                            </div>
                          </div>
                          <div className="ml-auto flex items-center space-x-2 pr-2">
                            <div className="transition-transform duration-200 group-radix-state-open:rotate-180">
                              <Icon
                                color="text-gray-500 group-hover:text-gray-700"
                                icon={faChevronDown}
                                size="xs"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Accordion.Trigger>
                  </Accordion.Header>

                  <Accordion.Content className="overflow-hidden radix-state-closed:animate-accordionSlideUpAndHideOverflow radix-state-open:animate-accordionSlideDownAndShowOverflow">
                    <div className="ml-[10px] mt-4 flex">
                      <div className="w-px bg-gray-200" />
                      <div className="ml-4 flex-1">
                        {/* "Other" group: providers without grouping_categories are stored as flat array */}
                        {Array.isArray(territories) && (
                          <div className="flex flex-wrap">
                            {territories.map((provider, idx) => {
                              const displayName =
                                getProviderDisplayName(provider);
                              return renderProviderCard(
                                provider,
                                displayName,
                                idx,
                                46,
                                idx === territories.length - 1,
                              );
                            })}
                          </div>
                        )}
                        {/* Level 2: territory groups (e.g. "US", "EU") - rendered as nested structure */}
                        {!Array.isArray(territories) &&
                          Object.entries(territories)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([territoryName, providers], index) => {
                              if (!Array.isArray(providers)) return null;

                              const territoryCount = providers.length;
                              const isFirstTerritory = index === 0;

                              // Get the actual territory display name
                              const firstProvider = providers[0];
                              const territoryTag =
                                getTerritoryTag(firstProvider);
                              const territoryDisplayName = territoryTag
                                ? getTerritoryDisplayName(territoryTag.value)
                                : territoryName;

                              // < 4 providers: render as flat cards with "Territory | Service" title
                              if (territoryCount < 4) {
                                return (
                                  <div
                                    key={territoryName}
                                    className={`${isFirstTerritory ? "" : "mt-4"} flex flex-wrap`}
                                  >
                                    {providers.map((provider, idx) => {
                                      const displayName =
                                        getProviderDisplayName(provider);
                                      const title = territoryDisplayName
                                        ? `${territoryDisplayName} | ${displayName}`
                                        : displayName;
                                      const regions = Array.isArray(
                                        territoryTag?.value,
                                      )
                                        ? territoryTag.value
                                        : [];

                                      const showRegionsToolTip =
                                        regions.length >
                                        DISPLAY_REGIONS_THRESHOLD;

                                      return renderProviderCard(
                                        provider,
                                        title,
                                        idx,
                                        46,
                                        idx === providers.length - 1,
                                        showRegionsToolTip,
                                        regions,
                                      );
                                    })}
                                  </div>
                                );
                              }

                              // >= 4 providers: render as nested accordion
                              return (
                                <Accordion.Item
                                  key={territoryName}
                                  className={isFirstTerritory ? "" : "mt-4"}
                                  value={`${providerName}-${territoryName}`}
                                >
                                  <div className="w-full">
                                    <Accordion.Header>
                                      <Accordion.Trigger className="group w-full">
                                        <div className="box-border h-full rounded-lg border border-gray-200 transition-all hover:cursor-pointer hover:border-indigo-400">
                                          <div
                                            className="flex items-center pl-2"
                                            style={{ height: `46px` }}
                                          >
                                            <div className="text-gray-800 font-inter-medium-12px">
                                              {territoryDisplayName &&
                                                `${territoryDisplayName} `}
                                              <span className="text-gray-500">
                                                ({territoryCount}{" "}
                                                {territoryCount === 1
                                                  ? "service"
                                                  : "services"}
                                                )
                                              </span>
                                            </div>
                                            <div className="ml-auto flex items-center space-x-2 pr-2">
                                              <div className="transition-transform duration-200 group-radix-state-open:rotate-180">
                                                <Icon
                                                  color="text-gray-500 group-hover:text-gray-700"
                                                  icon={faChevronDown}
                                                  size="xs"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </Accordion.Trigger>
                                    </Accordion.Header>

                                    <Accordion.Content className="overflow-hidden radix-state-closed:animate-accordionSlideUpAndHideOverflow radix-state-open:animate-accordionSlideDownAndShowOverflow">
                                      <div className="ml-[10px] mt-4 flex">
                                        <div className="w-px bg-gray-200" />
                                        <div className="ml-4 flex flex-1 flex-wrap">
                                          {providers.map((provider, idx) => {
                                            const displayName =
                                              getProviderDisplayName(provider);
                                            return renderProviderCard(
                                              provider,
                                              displayName,
                                              idx,
                                              46,
                                              idx === providers.length - 1,
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </Accordion.Content>
                                  </div>
                                </Accordion.Item>
                              );
                            })}
                      </div>
                    </div>
                  </Accordion.Content>
                </div>
              </Accordion.Item>
            );
          })}
      </Accordion.Root>

      <Divider />
      <div className="mt-4 text-gray-800 font-inter-semibold-14px">
        Databases
      </div>
      <div className="mt-5 flex flex-wrap justify-start text-right">
        {databaseProviders(AWSRegion).map((provider) => {
          return (
            <CreateConnectionCard
              key={providerKey(provider.key)}
              dataLoc={providerKey(provider.key)}
              height={56}
              isBeta={isBetaConnection(provider.key)}
              provider={provider.key}
              showIcon={true}
              title={provider.value}
              onClick={() => onProviderSelect(provider.key, false)}
            />
          );
        })}
      </div>
      <Divider />
      <div className="mt-4 text-gray-800 font-inter-semibold-14px">
        Other tools
      </div>
      <div className="mt-5 flex flex-wrap justify-start text-right">
        <CreateConnectionCard
          key={providerKey("custom")}
          dataLoc={providerKey("custom")}
          height={56}
          isBeta={false}
          provider="custom"
          showIcon={true}
          title="Custom Connection"
          onClick={() => onProviderSelect("custom", false)}
        />
        <CreateConnectionCard
          key={providerKey("webhook")}
          dataLoc={providerKey("webhook")}
          height={56}
          isBeta={false}
          provider="webhook"
          showIcon={true}
          title="Inbound Webhook"
          onClick={() => onProviderSelect("webhook", false)}
        />
        <CreateConnectionCard
          key={providerKey("retool")}
          dataLoc={providerKey("retool")}
          height={56}
          helpTooltip={{
            body: "The Retool connection can be used to use your Retool app within Taktile. This can be useful to e.g. embed dashboards or create a Case Management UI. Once created you can access your Retool app from the Taktile home screen on the top right corner.",
            title: "Retool embed",
            placement: "right",
            icon: faInfoCircle,
          }}
          isBeta={false}
          provider="retool"
          showIcon={true}
          title="Retool"
          onClick={() => onProviderSelect("retool", false)}
        />
      </div>
    </>
  );
};

export const CreateConnectionGrid: React.FC<CreateConnectionGridProps> = (
  props,
) => {
  return <CreateConnectionGridGrouped {...props} />;
};
